import * as qs from 'query-string';
import { useEffect, useState } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, ActionCenterActionCreators, AppActionCreators, IRmcrResponse, IRelativePerformanceResponse, IActionCenterCenterPerformanceImmediateAttentionAndComplianceResponse } from '@nkr/store/index';
import { ICenterPerformanceCompliance, ICenterPerformanceImmediateAttention, IRelativePerformance, IScoreCard } from '@nkr/models/center-performance';
import { IRpChainEndDeclineRatio } from '@nkr/models/center-performance/relative-performance';
import { IUser } from '@nkr/models/user';
import { Glyphicon, PanelGroup } from 'react-bootstrap';
import { CenterPerformanceWrapper } from './styles';
import ScoreCard from './score-card';
import RelativePerformance from './relative-performance';
import ImmediateAttention from './immediate-attention';
import DataComplianceIssues from './data-compliance-issues';

interface IProps {
    centerId: number
}

interface IStateProps {
    user: IUser
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getRmcr: (centerId: number, startDate: string, endDate: string) => Promise<IRmcrResponse>,
    getRelativePerformance: (centerId: number, startDate: string, endDate: string) => Promise<IRelativePerformanceResponse>,
    getImmediateAttentionAndCompliance: (centerId: number, startDate: string, endDate: string) => Promise<IActionCenterCenterPerformanceImmediateAttentionAndComplianceResponse>,
    setChainEndDeclineRatio: (chainEndDeclineRatio: IRpChainEndDeclineRatio) => void
}

type TProps = IStateProps & IDispatchProps & IProps;

const CenterPerformance = (props: TProps) => {
    const [scoreCards, setScoreCards] = useState<IScoreCard[]>([]);
    const [immediateAttention, setImmediateAttention] = useState<ICenterPerformanceImmediateAttention>(null);
    const [compliance, setCompliance] = useState<ICenterPerformanceCompliance>(null);
    const [relativePerformance, setRelativePerformance] = useState<IRelativePerformance>(null);
    const [memberCenterCount, setMemberCenterCount] = useState(0);
    const [rmcrRunTimeSeconds, setRmcrRunTimeSeconds] = useState(0);
    const [relativePerformanceRunTimeSeconds, setRelativePerformanceRunTimeSeconds] = useState(0);
    const [immediateActionComplianceRunTimeSeconds, setImmediateActionComplianceunTimeSeconds] = useState(0);
    const [recipientsDonorsExpanded, setRecipientsDonorsExpanded] = useState(false);
    const [postTxpRecipientsDonorsExpanded, setPostTxpRecipientsDonorsExpanded] = useState(false);
    const [micrositeRecipientsExpanded, setMicrositeRecipientsExpanded] = useState(false);
    const [loadingScoreCards, setLoadingScoreCards] = useState(true);
    const [loadingRelativePerformance, setLoadingRelativePerformance] = useState(true);
    const [loadingImmediateAttentionAndCompliance, setLoadingImmediateAttentionAndCompliance] = useState(true);

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        let parsed = qs.parse(window.location.search)
        let startDate = parsed.startDate ? parsed.startDate : '';
        let endDate = parsed.endDate ? parsed.endDate : '';

        props.getRmcr(props.centerId, startDate, endDate).then(response => {
            let { data, runTimeSeconds } = response.data.results;

            setScoreCards(data);
            setRmcrRunTimeSeconds(runTimeSeconds)
            setLoadingScoreCards(false);
        });

        props.getRelativePerformance(props.centerId, startDate, endDate).then(response => {
            let { relativePerformance, memberCenterCount, runTimeSeconds } = response.data.results;

            setRelativePerformance(relativePerformance);
            setMemberCenterCount(memberCenterCount);
            setRelativePerformanceRunTimeSeconds(runTimeSeconds)
            setLoadingRelativePerformance(false);
            props.setChainEndDeclineRatio(relativePerformance.chainEndDeclineRatio);
        });

        props.getImmediateAttentionAndCompliance(props.centerId, startDate, endDate).then(response => {
            let { immediateAttention, compliance, runTimeSeconds } = response.data.results;

            console.log(response.data.results);
            setImmediateAttention(immediateAttention);
            setCompliance(compliance)
            setImmediateActionComplianceunTimeSeconds(runTimeSeconds);
            setLoadingImmediateAttentionAndCompliance(false);
        });
    };

    const handleScrollToComplianceItems = (element: string) => {
        let e = document.getElementById(element);

        setRecipientsDonorsExpanded(true);
        setPostTxpRecipientsDonorsExpanded(true);
        setMicrositeRecipientsExpanded(true);

        setTimeout(() => {
            e.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
                inline: 'start',
            });
        }, 500);
    };

    const handleToggleRecipientDonorsExpanded = () => {
        setRecipientsDonorsExpanded(!recipientsDonorsExpanded);
    };

    const handleTogglePostTxpRecipientsDonorsExpanded = () => {
        setPostTxpRecipientsDonorsExpanded(!postTxpRecipientsDonorsExpanded);
    };

    const handleToggleMicrositeRecipientsExpanded = () => {
        setMicrositeRecipientsExpanded(!micrositeRecipientsExpanded);
    };

    const handleRefresh = () => {
        getData();
    };

    const totalRunTimeSeconds = rmcrRunTimeSeconds + relativePerformanceRunTimeSeconds + immediateActionComplianceRunTimeSeconds;

    return (
        <>
            <CenterPerformanceWrapper className='container-fluid'>
                {__ENVIRONMENT__ == 'dev' ? <div>{totalRunTimeSeconds} <Glyphicon glyph='refresh' onClick={handleRefresh} /></div> : null}

                <PanelGroup>
                    <ScoreCard
                        data={scoreCards}
                        centerId={props.centerId}
                        loading={loadingScoreCards}
                        onScrollToComplianceItems={handleScrollToComplianceItems}
                    />

                    <RelativePerformance
                        data={relativePerformance != null ? relativePerformance : null}
                        memberCenterCount={memberCenterCount}
                        centerId={props.centerId}
                        isSuperUser={props.user.group.isSuper}
                        loading={loadingRelativePerformance}
                    />

                    <ImmediateAttention
                        centerId={props.centerId}
                        data={immediateAttention}
                        loading={loadingImmediateAttentionAndCompliance}
                    />

                    <DataComplianceIssues
                        data={compliance}
                        centerId={props.centerId}
                        recipientsDonorsExpanded={recipientsDonorsExpanded}
                        postTxpRecipientsDonorsExpanded={postTxpRecipientsDonorsExpanded}
                        micrositeRecipientsExpanded={micrositeRecipientsExpanded}
                        loading={loadingImmediateAttentionAndCompliance}
                        onRecipientsDonorsExpanded={handleToggleRecipientDonorsExpanded}
                        onPostTxpRecipientsDonorsExpanded={handleTogglePostTxpRecipientsDonorsExpanded}
                        onMicrositeRecipientsExpanded={handleToggleMicrositeRecipientsExpanded}
                    />
                </PanelGroup>
            </CenterPerformanceWrapper>
        </>
    );
}

const mapStateToProps = (appState: IApplicationState, ownProps: IProps): IStateProps => {
    return {
        user: appState.app.user
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        // getCenterPerformance: (centerId, startDate, endDate) => {
        //     return dispatch(ActionCenterActionCreators.getCenterPerformance(centerId, startDate, endDate));
        // },
        getRmcr: (centerId, startDate, endDate) => {
            return dispatch(ActionCenterActionCreators.getRmcr(centerId, startDate, endDate));
        },
        getRelativePerformance: (centerId, startDate, endDate) => {
            return dispatch(ActionCenterActionCreators.getRelativePerformance(centerId, startDate, endDate));
        },
        getImmediateAttentionAndCompliance: (centerId, startDate, endDate) => {
            return dispatch(ActionCenterActionCreators.getImmediateAttentionAndCompliance(centerId, startDate, endDate));
        },
        setChainEndDeclineRatio: (chainEndDeclineRatio) => {
            dispatch(ActionCenterActionCreators.setChainEndDeclineRatio(chainEndDeclineRatio));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterPerformance);